import { useCallback, useMemo, useRef } from 'react';
import AxiosService from '__services/AxiosService';
import { TFunction, useTranslation } from 'react-i18next';

import { useSelectTypeaheadWithScroll } from '__components/Inputs/Select/hooks';

import Select from '__components/Inputs/Select';
import { GenericOption } from '__pages/SmartConnectors/types';
import { searchEntityRecords } from '@kizen/api/records';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useSelector } from 'react-redux';

type RecordOption = GenericOption;

type RecordSelectProps = {
  customObjectId: string;
  value: RecordOption;
  onChange: (value: string) => void;
  label: string;
  labelInfo?: string;
  placeholder?: string;
  error: boolean;
  validate: any;
  variant: 'underline' | 'outline';
  menuInline?: boolean;
  menuLeftButton?: any;
  menuRightButton?: any;
};

const fieldToOption =
  (t: TFunction) =>
  ({
    id,
    fields,
  }: {
    id: string;
    fields: Record<string, { value: string; name: string }>;
  }) => {
    const { display_name, last_name, first_name, email } = Object.values(
      fields
    ).reduce(
      (acc, { name, value }) => {
        if (
          ['display_name', 'last_name', 'first_name', 'email'].includes(name)
        ) {
          return {
            ...acc,
            [name]: name === 'email' && value ? ` (${value})` : value,
          };
        }
        return acc;
      },
      { display_name: null, last_name: null, first_name: null, email: null }
    );

    return {
      value: id,
      label: display_name
        ? display_name
        : `${[first_name, last_name, email].filter(Boolean).join(' ')}` ||
          t('Unknown'),
    };
  };

export const RecordSelect = ({
  customObjectId,
  value,
  onChange,
  label,
  labelInfo,
  placeholder,
  error,
  validate,
  variant,
  menuInline,
  menuLeftButton,
  menuRightButton,
}: RecordSelectProps) => {
  const { t } = useTranslation();

  const selectRef = useRef(null);

  const params = useMemo(
    () => ({
      active: true,
      custom_object_id: customObjectId,
    }),
    [customObjectId]
  );
  const contactObject = useSelector(getBusinessClientObject);

  const searchRecords = useCallback(
    ({ search, page }: { search: string; page: number }) => {
      return customObjectId
        ? searchEntityRecords(AxiosService, customObjectId, {
            params: {
              page_size: 20,
              page,
              search,
              ordering:
                customObjectId === contactObject.id ? 'full_name' : 'name',
            },
          })
        : { count: 0, results: [], next: null, previous: null };
    },
    [customObjectId, contactObject]
  );

  const [typeaheadProps, { loading }] = useSelectTypeaheadWithScroll({
    enabled: !!customObjectId,
    fetch: searchRecords,
    selectRef,
    params,
    chosenValueIds: [],
    objectToOption: fieldToOption(t),
    keepPreviousData: false,
  });

  return (
    <Select
      ref={selectRef}
      value={value}
      label={label}
      labelInfo={labelInfo}
      labelInfoPlacement="top"
      labelInfoMaxWidth={255}
      onChange={onChange}
      placeholder={placeholder || t('Select Record')}
      {...typeaheadProps}
      isLoading={loading}
      margin
      error={error}
      validate={validate}
      variant={variant}
      menuInline={menuInline}
      menuLeftButton={menuLeftButton}
      menuRightButton={menuRightButton}
    />
  );
};
